<template>
  <div class="case-detail-page" :class="{ 'is-mobile': isMobileRoute }">
    <div class="detail-shell">
      <div v-if="isMobileRoute && isPageLoading" class="mobile-case-hero mobile-case-hero-skeleton" aria-hidden="true">
        <div class="case-detail-loading-block mobile-case-hero-loading"></div>
      </div>

      <div v-else-if="isMobileRoute" class="mobile-case-hero">
        <div
          class="mobile-case-slider"
          @touchstart.passive="handleHeroTouchStart"
          @touchend="handleHeroTouchEnd"
        >
          <div
            v-if="!isMobileHeroImageLoaded"
            class="case-detail-loading-block mobile-case-hero-loading mobile-case-hero-loading-overlay"
            aria-hidden="true"
          ></div>
          <img
            v-if="hasMobileHeroImage"
            :key="mobileActiveImageUrl"
            :src="mobileActiveImageUrl"
            :alt="mobileActiveTypeName || caseDetail.title || '案例详情'"
            :class="{ loaded: isMobileHeroImageLoaded }"
            @load="markMobileHeroImageLoaded"
            @error="handleMobileHeroImageError"
          />
          <div v-if="isMobileHeroImageLoaded && mobileActiveImages.length > 1" class="mobile-carousel-dots" aria-label="案例图片切换">
            <button
              v-for="(img, index) in mobileActiveImages"
              :key="img.url || index"
              type="button"
              class="mobile-carousel-dot"
              :class="{ active: index === activeCaseImageIndex }"
              :aria-label="`查看第${index + 1}张图片`"
              @click.stop="setActiveCaseImage(index)"
            ></button>
          </div>
        </div>
        <div v-if="caseImages.length" class="mobile-space-tabs">
          <button
            v-for="(group, index) in caseImages"
            :key="`${group.typeName}-${index}`"
            type="button"
            :class="{ active: index === activeCaseImgIndex }"
            @click="setActiveCaseImg(index)"
          >
            {{ group.typeName }}
          </button>
        </div>
      </div>

      <div v-if="!isMobileRoute && !isPageLoading" class="breadcrumb">
        <span @click="goToCaseList">案例</span>
        <span class="breadcrumb-separator">&gt;</span>
        <span>{{ caseDetail.title || '---' }}</span>
      </div>

      <div class="case-card">
        <template v-if="isPageLoading">
          <div class="case-detail-loading-block case-detail-loading-title"></div>
          <div class="case-detail-loading-row">
            <div class="case-detail-loading-block case-detail-loading-meta"></div>
            <div class="case-detail-loading-block case-detail-loading-meta short"></div>
          </div>
          <div class="info-box case-detail-info-loading">
            <div v-for="index in 7" :key="`case-info-skeleton-${index}`" class="case-detail-loading-block case-detail-loading-info"></div>
          </div>
          <div class="case-detail-loading-block case-detail-loading-image"></div>
          <div class="related-grid case-detail-related-loading">
            <div v-for="index in 4" :key="`case-related-skeleton-${index}`" class="case-detail-loading-block case-detail-loading-related"></div>
          </div>
        </template>

        <template v-else>
          <h1 class="case-title">
          <span v-if="isMobileRoute && caseDetail.isOrder" class="mobile-case-badge">
            <img src="https://obs.huahuaeco.com/official/diamand-logo.png" alt="">
            <span>平台交易案例</span>
          </span>
          <span>{{ caseDetail.title }}</span>
        </h1>

        <div v-if="!isMobileRoute" class="case-meta">
          <span>发布时间：{{ caseDetail.createTime }}</span>
          <span class="meta-divider"></span>
          <span>发布人：{{ caseDetail.nickName || '---' }}</span>
        </div>

        <div class="info-box">
          <div class="info-item info-item-location">
            <div class="info-inline">
              <span class="info-location-value">{{ caseDetail.residenceAddress || '---' }}</span>
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">房屋类型</div>
            <div class="info-value">{{ formatDictValue(caseDetail.residenceType) }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">房屋户型</div>
            <div class="info-value">{{ caseDetail.houseType || '---' }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">房屋面积</div>
            <div class="info-value">{{ caseDetail.acreage || '---' }}㎡</div>
          </div>
          <div class="info-item">
            <div class="info-label">装修类型</div>
            <div class="info-value">{{ formatDictValue(caseDetail.decorationType) }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">装修风格</div>
            <div class="info-value">{{ formatDictValue(caseDetail.designStyle) }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">{{caseDetail.issuerUserType == '03' ? '设计': caseDetail.issuerUserType == '05'? '造价' : '装修'}}费用</div>
            <div class="info-value">{{ formatCost(caseDetail.cost) }}</div>
          </div>
        </div>

        <section v-if="caseDescription" class="section design-section">
          <h3 class="section-title">案例描述</h3>
          <p class="design-desc">{{ caseDescription }}</p>
        </section>

        <div v-if="isMobileRoute" class="case-meta mobile-case-meta">
          <span>发布时间：{{ caseDetail.createTime || '---' }}</span>
          <span>发布人：{{ caseDetail.nickName || '---' }}</span>
        </div>

        <template v-if="!isMobileRoute">
          <section v-for="caseImg in caseImages" :key="caseImg.typeName" class="section">
            <h3 class="section-title">{{ caseImg.typeName }}</h3>
            <img v-for="img in caseImg.imgList" :key="img.url" class="hero-image" :src="img.url" :alt="caseImg.typeName" />
          </section>
        </template>

        <section class="section related">
          <h3 class="section-title">看看其他案例</h3>
          <div class="related-grid">
            <div v-for="(c, idx) in relatedCases" :key="idx" class="related-item" @click="goToRelatedCase(c)">
              <div class="related-cover">
                <img :src="c.coverImg || 'https://obs.huahuaeco.com/official/product2.png'" alt="相关案例" />
                <div v-if="c.tagText" class="related-tags-line">{{ c.tagText }}</div>
              </div>
              <h3 class="related-title">
                <span v-if="c.isOrder" class="related-badge">
                  <img src="https://obs.huahuaeco.com/official/diamand-logo.png" alt="化化钻石会员">
                  <span>平台交易案例</span>
                </span>
                <span class="related-title-text">{{ c.title }}</span>
              </h3>
              <div v-if="!isMobileRoute && c.tagText" class="related-tags">{{ c.tagText }}</div>
            </div>
          </div>
        </section>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { markRaw, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { getCaseDetail,getUserCasePageList } from '@/api/case'
import { CASE_DETAIL_DEFAULT_TITLE, getCaseDetailSeoTitle } from '@/utils/seo-route-data'
import { getPageSsgState, setPageSsgState } from '@/utils/ssg-state'

const SSG_STATE_KEY = 'caseDetailPage'

const CASE_DETAIL_MAP = {
  'case-1': {
    title: '现代简约横厅案例，空间动线与采光层次同步提升',
    publishedAt: '2026-05-28',
    author: { name: '化化案例库' },
    location: '深圳市 · 南山区',
    area: '69㎡',
    style: '现代轻奢',
    budget: '约18万',
    concept: '通过横厅布局重组公共区域关系，以浅暖色材质和线性灯光提升空间通透感，让小面积也具备更完整的会客与收纳体验。',
    images: [
      'https://obs.huahuaeco.com/official/product2.png',
      'https://obs.huahuaeco.com/official/product2.png'
    ]
  }
}

function createDefaultCaseDetail(id) {
  return {
    title: id ? `案例标题 - ${id}` : '案例标题案例标题',
    publishedAt: '2026-05-28',
    author: { name: '化化案例库' },
    location: '深圳市 · 宝安区',
    layout: '三室两厅',
    area: '120㎡',
    style: '现代简约',
    budget: '约30万',
    packageType: '半包',
    concept: '本案以简洁大气为主，运用中性配色和材质对比，营造温馨且有格调的居住氛围。',
    images: [
      'https://obs.huahuaeco.com/official/product2.png',
      'https://obs.huahuaeco.com/official/product2.png'
    ]
  }
}

function buildCaseDetailSeoTitle(title) {
  return title
    ? `${title} - 业主装修案例_自建房案例-化化网`
    : CASE_DETAIL_DEFAULT_TITLE
}

export default {
  name: 'CaseDetailPage',
  setup() {
    const route = useRoute()
    const caseId = route.params.id || route.query.id
    const pageTitle = ref(getCaseDetailSeoTitle(caseId))

    useHead(() => ({
      title: pageTitle.value,
      meta: [
        { name: 'description', content: '化化网真实装修案例、自建房案例的详情内容，覆盖旧房翻新、新房整装、局部改造、自建房翻新与新建等。看效果图、实景图，读设计理念，获取设计灵感。' },
        { name: 'keywords', content: '装修案例,实景案例,旧房翻新案例,自建房案例,化化网' }
      ]
    }))

    return {
      pageTitle
    }
  },
  data() {
    return {
      caseDetail: {},
      relatedCases: [],
      caseId: null,
      activeCaseImgIndex: 0,
      activeCaseImageIndex: 0,
      caseImageTimer: null,
      heroTouchStartX: 0,
      heroTouchStartY: 0,
      isPageLoading: true,
      mobileHeroLoadedMap: {},
      mobileHeroPreloadMap: {}
    }
  },
  created() {
    this.applySsgState()
  },
  mounted() {
    this.refreshPageData()
  },
  beforeUnmount() {
    this.stopCaseImageAutoPlay()
  },
  serverPrefetch() {
    return this.syncPageData()
  },
  computed: {
    isMobileRoute() {
      return this.$route.path === '/m/case/detail' || this.$route.path.startsWith('/m/case/detail/')
    },
    caseImages() {
      if (!Array.isArray(this.caseDetail?.caseImgs)) {
        return []
      }

      return this.caseDetail.caseImgs
        .map((group, index) => ({
          ...group,
          typeName: group?.typeName || `空间${index + 1}`,
          imgList: Array.isArray(group?.imgList)
            ? group.imgList.filter((img) => img?.url)
            : []
        }))
        .filter((group) => group.imgList.length)
    },
    mobileCoverImg() {
      const firstCaseImg = this.caseImages.find((item) => Array.isArray(item.imgList) && item.imgList.length)
      return this.caseDetail?.coverImg
        || firstCaseImg?.imgList?.[0]?.url
        || ''
    },
    mobileActiveGroup() {
      return this.caseImages[this.activeCaseImgIndex] || null
    },
    mobileActiveImages() {
      const groupImages = this.mobileActiveGroup?.imgList || []
      if (groupImages.length) {
        return groupImages
      }

      return this.caseDetail?.coverImg ? [{ url: this.caseDetail.coverImg }] : []
    },
    mobileActiveImageUrl() {
      return this.mobileActiveImages[this.activeCaseImageIndex]?.url
        || this.mobileActiveImages[0]?.url
        || this.mobileCoverImg
    },
    hasMobileHeroImage() {
      return Boolean(this.mobileActiveImageUrl)
    },
    isMobileHeroImageLoaded() {
      return this.isMobileHeroImageUrlLoaded(this.mobileActiveImageUrl)
    },
    mobileHeroImageUrls() {
      const urls = []
      if (this.caseDetail?.coverImg) {
        urls.push(this.caseDetail.coverImg)
      }
      this.caseImages.forEach((group) => {
        const imgList = group.imgList || []
        imgList.forEach((img) => {
          if (img?.url) {
            urls.push(img.url)
          }
        })
      })
      return Array.from(new Set(urls.map((url) => this.normalizeImageUrl(url)).filter(Boolean)))
    },
    mobileActiveTypeName() {
      return this.mobileActiveGroup?.typeName || ''
    },
    caseDescription() {
      const candidates = [
        this.caseDetail?.designConcept,
        this.caseDetail?.designIdea,
        this.caseDetail?.designIdeas,
        this.caseDetail?.designDescription,
        this.caseDetail?.designDesc,
        this.caseDetail?.concept,
        this.caseDetail?.description,
        this.caseDetail?.caseDesc,
        this.caseDetail?.content,
        this.caseDetail?.remark
      ]
      const value = candidates.find((item) => item !== undefined && item !== null && String(item).trim())
      return value ? this.stripHtml(String(value)).trim() : ''
    },
    caseListPath() {
      return this.isMobileRoute ? '/m/case' : '/case'
    },
    caseDetailBasePath() {
      return this.isMobileRoute ? '/m/case/detail' : '/case/detail'
    },
    displayImages() {
      const fallbackImages = [
        'https://obs.huahuaeco.com/official/product2.png',
        'https://obs.huahuaeco.com/official/product2.png'
      ]
      const images = this.caseDetail && Array.isArray(this.caseDetail.images) ? this.caseDetail.images : []
      return [images[0] || fallbackImages[0], images[1] || images[0] || fallbackImages[1]]
    }
  },
  watch: {
    '$route.query': {
      handler() {
        this.refreshPageData()
      },
      deep: true
    },
    '$route.params': {
      handler() {
        this.refreshPageData()
      },
      deep: true
    }
  },
  methods: {
    applySsgState() {
      const state = getPageSsgState(this.$route, SSG_STATE_KEY)
      if (!state) {
        return
      }

      this.caseDetail = state.caseDetail || {}
      this.relatedCases = Array.isArray(state.relatedCases) ? state.relatedCases : []
      this.caseId = state.caseId || null
      this.pageTitle = state.pageTitle || this.pageTitle
      this.activeCaseImgIndex = state.activeCaseImgIndex || 0
      this.activeCaseImageIndex = state.activeCaseImageIndex || 0
    },
    persistSsgState() {
      setPageSsgState(this.$route, SSG_STATE_KEY, {
        caseDetail: this.caseDetail,
        relatedCases: this.relatedCases,
        caseId: this.caseId,
        pageTitle: this.pageTitle,
        activeCaseImgIndex: this.activeCaseImgIndex,
        activeCaseImageIndex: this.activeCaseImageIndex
      })
    },
    async syncPageData() {
      await this.getCaseDetailApi()
      await this.getUserCasePageListApi()
      this.persistSsgState()
    },
    async refreshPageData() {
      this.isPageLoading = true
      try {
        await this.syncPageData()
      } finally {
        this.isPageLoading = false
      }
    },
    goToCaseList() {
      this.$router.push({ path: this.caseListPath })
    },
    getCaseDetailApi() {
      const { id } = this.$route.query
      this.caseId = this.$route.params.id || id || ''
      if (!this.caseId) return Promise.resolve()

      return getCaseDetail(this.caseId).then(res => {
        if (res && res.data) {
          this.caseDetail = res.data
          this.pageTitle = buildCaseDetailSeoTitle(this.caseDetail.title)
          this.$nextTick(() => {
            this.resetMobileImageState()
            this.preloadMobileHeroImages()
            this.restartCaseImageAutoPlay()
          })
        }
      }).catch(err => {
        console.error('fetch case detail error', err)
      })
    },
    async getUserCasePageListApi() {
      try {
        const res = await getUserCasePageList({
          pageNum: 1,
          pageSize: 50
        })
        let { data } = res.data
        const relatedList = (Array.isArray(data) ? data : [])
          .filter((item) => String(item.id) !== String(this.caseId))
        if (typeof window !== 'undefined') {
          relatedList.sort(() => 0.5 - Math.random())
        }
        this.relatedCases = relatedList.slice(0, 4).map(item => {
          const tags = this.buildCaseTags(item)
          return {
            id: item.id,
            title: item.title,
            coverImg: item.coverImg || 'https://obs.huahuaeco.com/official/product2.png',
            isOrder: item.isOrder,
            tags,
            tagText: tags.join(' | ')
          }
        }) || []
      } catch (err) {
        console.error('fetch case list error', err)
      }
    },
    applyRouteCaseDetail(route) {
      const id = (route && route.params && route.params.id) || (route && route.query && route.query.id) || ''
      const matchedDetail = CASE_DETAIL_MAP[id]
      this.caseDetail = matchedDetail ? { ...matchedDetail } : createDefaultCaseDetail(id)
    },
    goToRelatedCase(caseItem) {
      this.$router.push({
        path: `${this.caseDetailBasePath}/${caseItem.id}`
      })
    },
    formatDictValue(value, fallback = '---') {
      if (value === undefined || value === null || value === '') {
        return fallback
      }

      if (typeof value === 'object') {
        return value.dictLabel || value.label || value.name || value.value || fallback
      }

      return value
    },
    safeToFixed(num, decimal = 2) {
      const digits = Number.isInteger(Number(decimal)) && Number(decimal) >= 0 ? Number(decimal) : 2;
      const value = Number(num);
      if (!Number.isFinite(value)) {
        return (0).toFixed(digits);
      }

      const rawText = typeof num === 'string' ? num.trim() : '';
      const normalizedText = rawText && /^[-+]?\d+(?:\.\d+)?$/.test(rawText)
        ? rawText
        : value.toFixed(Math.min(digits + 12, 20)).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');

      const isNegative = normalizedText.startsWith('-');
      const unsignedText = normalizedText.replace(/^[+-]/, '');
      let [integerPart, fractionalPart = ''] = unsignedText.split('.');
      integerPart = integerPart.replace(/^0+(?=\d)/, '') || '0';

      const keptFraction = fractionalPart.slice(0, digits).padEnd(digits, '0');
      const nextDigit = fractionalPart[digits] || '0';
      let mergedValue = `${integerPart}${keptFraction}`.replace(/^0+(?=\d)/, '') || '0';
      let roundedInteger = BigInt(mergedValue);

      if (nextDigit >= '5') {
        roundedInteger += 1n;
      }

      const isZero = roundedInteger === 0n;
      const roundedText = roundedInteger.toString().padStart(digits + 1, '0');
      const roundedIntegerPart = digits === 0 ? roundedText : roundedText.slice(0, -digits) || '0';
      const roundedFractionalPart = digits === 0 ? '' : roundedText.slice(-digits);
      const sign = isNegative && !isZero ? '-' : '';

      if (digits === 0 || /^0+$/.test(roundedFractionalPart)) {
        return `${sign}${roundedIntegerPart}`;
      }

      return `${sign}${roundedIntegerPart}.${roundedFractionalPart}`;
    },
    formatCost(cost) {
      if (cost === undefined || cost === null || cost === '') return ''
      const costText = String(cost).trim()
      if (!costText) return ''
      if (/元|万/.test(costText)) return costText
      const costNumber = Number(costText)
      if (!Number.isFinite(costNumber)) return costText
      // if (costNumber >= 10000) return `${Number((costNumber / 10000).toFixed(2))}万`
      if (costNumber >= 10000) {
        return `${this.safeToFixed(costNumber / 10000)}万元`
      }
      return `${costText}元`
    },
    buildCaseTags(item) {
      const area = item?.acreage ? `${item.acreage}m²` : ''
      const style = this.formatDictValue(item?.designStyle, '')
      const houseType = item?.houseType || '' 
      const cost = this.formatCost(item?.cost, '')
      const decorationType = this.formatDictValue(item?.decorationType, '')
      return [area, style, houseType, cost || decorationType].filter(Boolean)
    },
    stripHtml(value = '') {
      return String(value).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ')
    },
    resetMobileImageState() {
      if (!this.caseImages.length) {
        this.activeCaseImgIndex = 0
        this.activeCaseImageIndex = 0
        return
      }

      if (this.activeCaseImgIndex >= this.caseImages.length) {
        this.activeCaseImgIndex = 0
      }

      const currentImages = this.caseImages[this.activeCaseImgIndex]?.imgList || []
      if (this.activeCaseImageIndex >= currentImages.length) {
        this.activeCaseImageIndex = 0
      }
    },
    setActiveCaseImg(index) {
      if (index < 0 || index >= this.caseImages.length) {
        return
      }

      this.activeCaseImgIndex = index
      this.activeCaseImageIndex = 0
      this.preloadMobileHeroImages()
      this.restartCaseImageAutoPlay()
    },
    setActiveCaseImage(index) {
      if (index < 0 || index >= this.mobileActiveImages.length) {
        return
      }

      this.activeCaseImageIndex = index
      this.preloadMobileHeroImages()
      this.restartCaseImageAutoPlay()
    },
    nextCaseImage() {
      const count = this.mobileActiveImages.length
      if (count <= 1) {
        return
      }

      this.activeCaseImageIndex = (this.activeCaseImageIndex + 1) % count
      this.preloadMobileHeroImages()
    },
    prevCaseImage() {
      const count = this.mobileActiveImages.length
      if (count <= 1) {
        return
      }

      this.activeCaseImageIndex = (this.activeCaseImageIndex - 1 + count) % count
      this.preloadMobileHeroImages()
    },
    normalizeImageUrl(url) {
      return url ? String(url).trim() : ''
    },
    isMobileHeroImageUrlLoaded(url) {
      const normalizedUrl = this.normalizeImageUrl(url)
      return !normalizedUrl || Boolean(this.mobileHeroLoadedMap[normalizedUrl])
    },
    markMobileHeroImageUrlLoaded(url) {
      const normalizedUrl = this.normalizeImageUrl(url)
      if (!normalizedUrl || this.mobileHeroLoadedMap[normalizedUrl]) {
        return
      }

      this.mobileHeroLoadedMap = {
        ...this.mobileHeroLoadedMap,
        [normalizedUrl]: true
      }
    },
    clearMobileHeroPreloader(url) {
      const normalizedUrl = this.normalizeImageUrl(url)
      if (!normalizedUrl || !this.mobileHeroPreloadMap[normalizedUrl]) {
        return
      }

      const nextPreloadMap = { ...this.mobileHeroPreloadMap }
      delete nextPreloadMap[normalizedUrl]
      this.mobileHeroPreloadMap = nextPreloadMap
    },
    preloadMobileHeroImage(url) {
      const normalizedUrl = this.normalizeImageUrl(url)
      if (
        typeof window === 'undefined'
        || !normalizedUrl
        || this.mobileHeroLoadedMap[normalizedUrl]
        || this.mobileHeroPreloadMap[normalizedUrl]
      ) {
        return
      }

      const image = markRaw(new window.Image())
      image.decoding = 'async'
      image.onload = () => {
        this.markMobileHeroImageUrlLoaded(normalizedUrl)
        this.clearMobileHeroPreloader(normalizedUrl)
      }
      image.onerror = () => {
        this.clearMobileHeroPreloader(normalizedUrl)
      }
      this.mobileHeroPreloadMap = {
        ...this.mobileHeroPreloadMap,
        [normalizedUrl]: image
      }
      image.src = normalizedUrl

      if (image.complete && image.naturalWidth > 0) {
        this.markMobileHeroImageUrlLoaded(normalizedUrl)
        this.clearMobileHeroPreloader(normalizedUrl)
      }
    },
    preloadMobileHeroImages() {
      if (typeof window === 'undefined' || !this.isMobileRoute) {
        return
      }

      this.mobileHeroImageUrls.forEach((url) => {
        this.preloadMobileHeroImage(url)
      })
    },
    markMobileHeroImageLoaded(event) {
      const target = event?.currentTarget
      this.markMobileHeroImageUrlLoaded(target?.currentSrc || target?.src || this.mobileActiveImageUrl)
    },
    handleMobileHeroImageError(event) {
      const target = event?.currentTarget
      this.clearMobileHeroPreloader(target?.currentSrc || target?.src || this.mobileActiveImageUrl)
    },
    startCaseImageAutoPlay() {
      if (typeof window === 'undefined' || !this.isMobileRoute || this.mobileActiveImages.length <= 1 || this.caseImageTimer) {
        return
      }

      this.caseImageTimer = window.setInterval(() => {
        this.nextCaseImage()
      }, 3600)
    },
    stopCaseImageAutoPlay() {
      if (typeof window === 'undefined' || !this.caseImageTimer) {
        return
      }

      window.clearInterval(this.caseImageTimer)
      this.caseImageTimer = null
    },
    restartCaseImageAutoPlay() {
      this.stopCaseImageAutoPlay()
      this.startCaseImageAutoPlay()
    },
    handleHeroTouchStart(event) {
      const touch = event.changedTouches && event.changedTouches[0]
      if (!touch) return
      this.heroTouchStartX = touch.clientX
      this.heroTouchStartY = touch.clientY
      this.stopCaseImageAutoPlay()
    },
    handleHeroTouchEnd(event) {
      const touch = event.changedTouches && event.changedTouches[0]
      if (!touch) {
        this.startCaseImageAutoPlay()
        return
      }

      const deltaX = touch.clientX - this.heroTouchStartX
      const deltaY = touch.clientY - this.heroTouchStartY
      if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX < 0) {
          this.nextCaseImage()
        } else {
          this.prevCaseImage()
        }
      }

      this.startCaseImageAutoPlay()
    }
  }
}
</script>

<style scoped lang="scss">
.case-detail-page {
  padding: 92px 0 80px;
  min-height: 100vh;
  background: #f3f5f9;
}

.detail-shell {
  width: min(1120px, calc(100% - 48px));
  margin: 0 auto;
}

.case-card {
  background: #fff;
  padding: 26px 30px 34px;
}

.case-detail-loading-block {
  border-radius: 10px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.55) 50%, rgba(255, 255, 255, 0) 100%), #efe7dd;
  background-size: 200% 100%;
  animation: case-detail-loading-shimmer 1.2s ease-in-out infinite;
}

.case-detail-loading-title {
  width: min(720px, 78%);
  height: 46px;
}

.case-detail-loading-row {
  display: flex;
  gap: 18px;
  margin-top: 20px;
}

.case-detail-loading-meta {
  width: 180px;
  height: 14px;
}

.case-detail-loading-meta.short {
  width: 130px;
}

.case-detail-info-loading {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.case-detail-loading-info {
  height: 42px;
  margin: 4px 12px;
}

.case-detail-loading-image {
  width: 80%;
  min-height: 420px;
  margin: 32px auto 0;
}

.case-detail-related-loading {
  margin-top: 34px;
}

.case-detail-loading-related {
  min-height: 240px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
  color: #9aa0a9;
  font-size: 12px;
  line-height: 1.4;
}

.breadcrumb span:nth-child(1) {
  cursor: pointer;
}

.breadcrumb-separator {
  color: #b7bcc5;
}

.case-title {
  margin: 0;
  color: #2f3135;
  font-size: 34px;
  line-height: 1.35;
  font-weight: 600;
}

.case-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 18px;
  margin-top: 18px;
  color: #979ca5;
  font-size: 12px;
  line-height: 1.4;
}

.meta-divider {
  width: 1px;
  height: 12px;
  background: #d7dce3;
}

.author-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e9f2ff;
  color: #3d89ff;
  font-size: 11px;
}

.info-box {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0;
  margin-top: 20px;
  padding: 12px 0;
  background: #f7f8fa;
}

.info-item {
  min-width: 0;
  padding: 4px 16px;
  border-right: 1px solid #ebedf1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.info-item:last-child {
  border-right: 0;
}

.info-item-location {
  grid-column: 1 / -1;
  align-items: flex-start;
  border-right: 0;
  margin-bottom: 2px;
  span {
    color: #262626 !important;
    font-size: 16px !important;
  }
}

.info-inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  min-height: 38px;
}

.info-label {
  color: #a0a6af;
  font-size: 12px;
  line-height: 1.4;
}

.info-location-value {
  color: #333;
  font-size: 12px;
  line-height: 1.5;
}

.info-value {
  margin-top: 8px;
  color: #333;
  font-size: 12px;
  line-height: 1.5;
}

.section {
  margin-top: 30px;
}

.section-title {
  margin: 0 0 14px;
  color: #222;
  font-size: 28px;
  line-height: 1.35;
  font-weight: 600;
}

.design-desc {
  margin: 0;
  color: #666c75;
  font-size: 12px;
  line-height: 1.8;
}

.hero-image {
  display: block;
  width: 80%;
  height: auto;
  background: #f3f0eb;
  margin: 0 auto 22px;
  border-radius: 10px;
}

.related {
  margin-top: 36px;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px 20px;
  margin-top: 20px;
}

.related-item {
  min-width: 0;
  cursor: pointer;
}

.related-cover {
  position: relative;
  overflow: hidden;
  background: #f3f0eb;
  border-radius: 10px;
}

.related-cover > img {
  display: block;
  width: 100%;
  aspect-ratio: 1 / 0.68;
  object-fit: cover;
}

.related-tags-line {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 26px 10px 8px;
  color: #fff;
  font-size: 13px;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.62) 100%);
}

.related-title {
  margin: 0;
  color: #333;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.55;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  overflow: hidden;
  margin-top: 12px;
}

.related-badge {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 12px;
  background: #e8f3ff;
}

.related-badge img {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.related-badge span {
  color: #4090ff;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
}

.related-title-text {
  min-width: 0;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.related-tags {
  color: #8c8c8c;
  font-size: 12px;
  line-height: 1.45;
  margin-top: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@keyframes case-detail-loading-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.case-detail-page.is-mobile {
  min-height: 100vh;
  padding: calc(88 * var(--rpx)) 0 calc(48 * var(--rpx));
  background: #fff;

  .detail-shell {
    width: 100%;
    margin: 0;
  }

  .mobile-case-hero {
    position: relative;
    min-height: calc(620 * var(--rpx));
    background: #e8edf2;
    overflow: hidden;
  }

  .mobile-case-hero-skeleton {
    display: flex;
  }

  .mobile-case-hero-loading {
    width: 100%;
    min-height: calc(620 * var(--rpx));
    border-radius: 0;
  }

  .mobile-case-hero-loading-overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    min-height: auto;
    height: 100%;
  }

  .mobile-case-slider {
    position: relative;

    img {
      display: block;
      position: relative;
      z-index: 0;
      width: 100%;
      height: calc(620 * var(--rpx));
      object-fit: cover;
      opacity: 0;
      transition: opacity 0.24s ease;
    }

    img.loaded {
      opacity: 1;
    }
  }

  .mobile-carousel-dots {
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(78 * var(--rpx));
    z-index: 2;
    display: flex;
    justify-content: center;
    gap: calc(10 * var(--rpx));
  }

  .mobile-carousel-dot {
    width: calc(12 * var(--rpx));
    height: calc(12 * var(--rpx));
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.68);
    transition: width 0.22s ease, background 0.22s ease;
  }

  .mobile-carousel-dot.active {
    width: calc(30 * var(--rpx));
    background: #fff;
  }

  .mobile-space-tabs {
    position: absolute;
    left: calc(18 * var(--rpx));
    right: calc(18 * var(--rpx));
    bottom: calc(18 * var(--rpx));
    display: flex;
    gap: calc(10 * var(--rpx));
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    button {
      flex: 0 0 auto;
      padding: calc(6 * var(--rpx)) calc(16 * var(--rpx));
      border: 0;
      border-radius: calc(999 * var(--rpx));
      color: #262626;
      font-size: calc(20 * var(--rpx));
      line-height: 1.2;
      background: rgba(255, 255, 255, 0.92);
    }

    button.active {
      color: #198cfe;
      font-weight: 700;
      background: #fff;
    }
  }

  .case-card {
    position: relative;
    z-index: 3;
    padding: calc(18 * var(--rpx)) calc(24 * var(--rpx)) calc(42 * var(--rpx));
    background: #fff;
  }

  .case-detail-loading-title {
    width: 84%;
    height: calc(42 * var(--rpx));
  }

  .case-detail-loading-row {
    gap: calc(14 * var(--rpx));
    margin-top: calc(22 * var(--rpx));
  }

  .case-detail-loading-meta {
    width: calc(190 * var(--rpx));
    height: calc(22 * var(--rpx));
  }

  .case-detail-info-loading {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .case-detail-loading-info {
    height: calc(54 * var(--rpx));
    margin: calc(4 * var(--rpx));
  }

  .case-detail-loading-image {
    width: 100%;
    min-height: calc(420 * var(--rpx));
    margin-top: calc(34 * var(--rpx));
  }

  .case-detail-related-loading {
    display: flex;
    flex-direction: column;
    gap: calc(24 * var(--rpx));
    margin-top: calc(42 * var(--rpx));
  }

  .case-detail-loading-related {
    min-height: calc(300 * var(--rpx));
  }

  .case-title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: calc(8 * var(--rpx));
    color: #262626;
    font-size: calc(28 * var(--rpx));
    font-weight: 700;
    line-height: 1.42;
  }

  .mobile-case-badge {
    display: inline-flex;
    align-items: center;
    gap: calc(4 * var(--rpx));
    padding: calc(4 * var(--rpx)) calc(10 * var(--rpx));
    border-radius: calc(999 * var(--rpx));
    color: #4090ff;
    font-size: calc(20 * var(--rpx));
    font-weight: 600;
    line-height: 1;
    background: #e8f3ff;

    img {
      width: calc(22 * var(--rpx));
      height: calc(22 * var(--rpx));
      object-fit: contain;
    }
  }

  .case-meta {
    gap: calc(10 * var(--rpx));
    margin-top: calc(18 * var(--rpx));
    color: #8c8c8c;
    font-size: calc(20 * var(--rpx));
  }

  .meta-divider {
    display: none;
  }

  .info-box {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: calc(20 * var(--rpx)) 0;
    margin-top: calc(18 * var(--rpx));
    padding: calc(22 * var(--rpx)) calc(18 * var(--rpx));
    border: 0;
    border-radius: calc(14 * var(--rpx));
    background: #f4f7fb;
  }

  .info-item {
    padding: 0 calc(4 * var(--rpx));
    border-right: 0;
  }

  .info-item-location {
    grid-column: 1 / -1;
    margin-bottom: calc(2 * var(--rpx));
    padding: 0 calc(4 * var(--rpx));

    span {
      color: #262626 !important;
      font-size: calc(28 * var(--rpx)) !important;
      font-weight: 700;
      line-height: 1.5;
    }
  }

  .info-inline {
    min-height: auto;
  }

  .info-label {
    color: #8c8c8c;
    font-size: calc(22 * var(--rpx));
    line-height: 1.4;
  }

  .info-value {
    margin-top: calc(8 * var(--rpx));
    color: #262626;
    font-size: calc(23 * var(--rpx));
    line-height: 1.45;
  }

  .section {
    margin-top: calc(34 * var(--rpx));
  }

  .section-title {
    margin-bottom: calc(16 * var(--rpx));
    color: #262626;
    font-size: calc(28 * var(--rpx));
    line-height: 1.35;
  }

  .design-section {
    margin-top: calc(30 * var(--rpx));
  }

  .design-desc {
    color: #8c8c8c;
    font-size: calc(24 * var(--rpx));
    line-height: 1.72;
  }

  .mobile-case-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: calc(20 * var(--rpx));
    margin-top: calc(30 * var(--rpx));
    color: #8c8c8c;
    font-size: calc(20 * var(--rpx));
    line-height: 1.4;
  }

  .hero-image {
    width: 100%;
    margin: 0 0 calc(18 * var(--rpx));
    border-radius: calc(8 * var(--rpx));
  }

  .related {
    margin-top: calc(42 * var(--rpx));
  }

  .related-grid {
    grid-template-columns: 1fr;
    gap: calc(24 * var(--rpx));
    margin-top: calc(18 * var(--rpx));
  }

  .related-item {
    background: transparent;
  }

  .related-cover {
    border-radius: calc(18 * var(--rpx));
  }

  .related-cover > img {
    aspect-ratio: 1 / 0.58;
    min-height: calc(300 * var(--rpx));
  }

  .related-tags-line {
    padding: calc(42 * var(--rpx)) calc(18 * var(--rpx)) calc(10 * var(--rpx));
    font-size: calc(26 * var(--rpx));
    line-height: 1.25;
  }

  .related-title {
    display: flex;
    align-items: center;
    gap: calc(10 * var(--rpx));
    margin-top: calc(14 * var(--rpx));
    color: #262626;
    font-size: calc(30 * var(--rpx));
    font-weight: 600;
    line-height: 1.35;
    overflow: visible;
  }

  .related-badge {
    padding: calc(3 * var(--rpx)) calc(8 * var(--rpx));
    border-radius: calc(999 * var(--rpx));
  }

  .related-badge img {
    width: calc(22 * var(--rpx));
    height: calc(22 * var(--rpx));
  }

  .related-badge span {
    font-size: calc(18 * var(--rpx));
  }

  .related-title-text {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .related-tags {
    display: none;
  }
}

@media (max-width: 768px) {
  .case-detail-page {
    padding: 76px 0 44px;
  }

  .detail-shell {
    width: calc(100% - 28px);
  }

  .case-card {
    padding: 18px 16px 24px;
  }

  .case-title {
    font-size: 24px;
  }

  .case-meta {
    gap: 8px 12px;
  }

  .meta-divider,
  .author-badge {
    display: none;
  }

  .info-box {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 0;
    padding: 14px 12px;
  }

  .info-item {
    padding: 0 12px;
    border-right: 0;
  }

  .info-item-location {
    grid-column: 1 / -1;
  }

  .section {
    margin-top: 24px;
  }

  .section-title {
    font-size: 22px;
    margin-bottom: 12px;
  }

  .related-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }
}

@media (max-width: 480px) {
  .case-title {
    font-size: 20px;
  }

  .info-box {
    grid-template-columns: 1fr;
  }

  .info-item-location {
    grid-column: auto;
  }
}
</style>
